import { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { isEqual } from 'lodash-es';
import {
  InlineLoading,
  InlineNotification,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Tag,
} from '@carbon/react';
import { PageHeader } from 'components/page-header';
import { NavigationGuard } from 'components/navigation-guard';
import { SuccessToast } from 'components/success-toast';
import ErrorToast from 'components/error-toast';
import { pageTitle } from 'utils/page-title';
import {
  ChangeBar,
  GuardrailsSection,
  StatusSection,
  ThresholdsSection,
  VocabularySections,
} from './config-sections';
import { DecisionLog } from './decision-log';
import { useAutoMatchConfig, useSaveConfiguration } from './hooks';
import { buildInput, seed, thresholdValidity, type FormState } from './utils';
import type { AutoMatchConfiguration } from './types';
import styles from './page.module.scss';

interface ConfigurationHeaderProps {
  baseline: AutoMatchConfiguration;
}

/**
 * The page header: title, on/off status and the active version.
 * @param {ConfigurationHeaderProps} props - The saved configuration
 * @return {JSX.Element} The header
 */
function ConfigurationHeader({ baseline }: ConfigurationHeaderProps) {
  return (
    <PageHeader
      breadcrumbs={[]}
      title="Auto-Match Configuration"
      titleDetail={
        <div className={styles.headerStatus}>
          <Tag type={baseline.enabled ? 'green' : 'gray'} size="sm">
            {baseline.enabled ? 'Enabled' : 'Disabled'}
          </Tag>
          {baseline.configVersion !== null && (
            <span className={styles.versionText}>
              Version {baseline.configVersion}
            </span>
          )}
        </div>
      }
      actions={null}
      compactContent
    />
  );
}

interface ConfigurationEditorProps {
  // The active configuration, from the config query; a save updates it there.
  baseline: AutoMatchConfiguration;
}

/**
 * The loaded page: the configuration form and the decision log as tabs, with
 * the unsaved-changes guard and save feedback.
 * @param {ConfigurationEditorProps} props - The active configuration
 * @return {JSX.Element} The page body
 */
function ConfigurationEditor({ baseline }: ConfigurationEditorProps) {
  const save = useSaveConfiguration();
  const [tabIndex, setTabIndex] = useState(0);
  const [form, setForm] = useState<FormState>(() => seed(baseline));
  const [savedVersion, setSavedVersion] = useState<number | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);

  /**
   * Applies form changes.
   * @param {Partial<FormState>} changes - The fields to change
   */
  const patch = (changes: Partial<FormState>) =>
    setForm((prev) => ({ ...prev, ...changes }));

  const dirty = !isEqual(buildInput(form), buildInput(seed(baseline)));
  const valuesValid = Object.values(thresholdValidity(form)).every(Boolean);
  const canSave =
    dirty && valuesValid && !!form.rationale.trim() && !save.isPending;

  /** Saves the form and reseeds from the server's new active configuration. */
  const onSave = () => {
    if (!canSave) return;
    setSaveError(null);
    save.mutate(
      { ...buildInput(form), rationale: form.rationale.trim() },
      {
        onSuccess: (updated) => {
          setForm(seed(updated));
          setSavedVersion(updated.configVersion);
        },
        onError: (error) => setSaveError(error.message),
      }
    );
  };

  return (
    <>
      <Helmet>
        <title>{pageTitle('Auto-Match Configuration')}</title>
      </Helmet>
      <NavigationGuard
        hasChanges={dirty}
        message="You have unsaved auto-match configuration changes. Discard them?"
      />
      <ConfigurationHeader baseline={baseline} />

      <Tabs
        selectedIndex={tabIndex}
        onChange={({ selectedIndex }) => setTabIndex(selectedIndex)}
      >
        <TabList aria-label="Auto-match sections" contained>
          <Tab>Configuration</Tab>
          <Tab>Decision log</Tab>
        </TabList>
        <TabPanels>
          <TabPanel>
            <div className={styles.contentWrapper}>
              <StatusSection form={form} patch={patch} />
              <ThresholdsSection
                form={form}
                patch={patch}
                candidateCap={baseline.candidateCap}
              />
              <GuardrailsSection
                form={form}
                patch={patch}
                guardrails={baseline.availableGuardrails}
              />
              <VocabularySections form={form} patch={patch} />
              <ChangeBar
                form={form}
                patch={patch}
                dirty={dirty}
                saving={save.isPending}
                canSave={canSave}
                onDiscard={() => setForm(seed(baseline))}
                onSave={onSave}
              />
            </div>
          </TabPanel>
          <TabPanel>{tabIndex === 1 && <DecisionLog />}</TabPanel>
        </TabPanels>
      </Tabs>

      {savedVersion !== null && (
        <SuccessToast
          message="Configuration saved"
          subtitle={`Now at version ${savedVersion}`}
          onClose={() => {
            setSavedVersion(null);
            return true;
          }}
        />
      )}
      {saveError && (
        <ErrorToast
          message="Could not save configuration"
          subtitle={saveError}
          onClose={() => {
            setSaveError(null);
            return true;
          }}
        />
      )}
    </>
  );
}

/**
 * The auto-match admin page: loads the active configuration, then edits it.
 * @return {JSX.Element} The page
 */
export function AutoMatchConfigurationPage() {
  const { data, isPending, isError } = useAutoMatchConfig();

  if (isPending) {
    return (
      <div className={styles.loading}>
        <InlineLoading description="Loading auto-match configuration…" />
      </div>
    );
  }
  if (isError) {
    return (
      <InlineNotification
        kind="error"
        hideCloseButton
        title="Failed to load"
        subtitle="The auto-match configuration could not be loaded. Refresh to try again."
      />
    );
  }
  return <ConfigurationEditor baseline={data} />;
}
